import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE_URL } from '../lib/config';
import type { OsmPlace } from '../lib/domain';

export interface MapBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

interface Props {
  center: { latitude: number; longitude: number } | null;
  places: OsmPlace[];
  onSelect: (place: OsmPlace) => void;
  onMoveEnd: (bounds: MapBounds) => void;
}

// Marker-Icon als einfaches DivIcon, damit keine Leaflet-Bilddateien gebündelt werden müssen.
const markerIcon = L.divIcon({ className: 'store-map-marker', html: '📍', iconSize: [24, 24], iconAnchor: [12, 22] });

// Karte mit freien OSM-Kacheln. Beim Verschieben/Zoomen wird über onMoveEnd neu gesucht.
export function StoreMap({ center, places, onSelect, onMoveEnd }: Props) {
  const el = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const moveRef = useRef(onMoveEnd);
  const selectRef = useRef(onSelect);
  moveRef.current = onMoveEnd;
  selectRef.current = onSelect;

  useEffect(() => {
    if (!el.current) return;
    const m = L.map(el.current).setView([51.1657, 10.4515], 6);
    L.tileLayer(`${API_BASE_URL}/tiles/{z}/{x}/{y}.png`, {
      maxZoom: 19,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende',
    }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    m.on('moveend', () => {
      const b = m.getBounds();
      moveRef.current({ south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() });
    });
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    if (center && map.current) map.current.setView([center.latitude, center.longitude], 16);
  }, [center]);

  useEffect(() => {
    const group = layer.current;
    if (!group) return;
    group.clearLayers();
    for (const p of places) {
      L.marker([p.latitude, p.longitude], { icon: markerIcon, title: p.displayName })
        .on('click', () => selectRef.current(p))
        .addTo(group);
    }
  }, [places]);

  return <div ref={el} style={{ height: 360, width: '100%', borderRadius: 8 }} role="application" aria-label="Karte mit Geschäften" />;
}
